(function () {
  'use strict';

  const STORAGE_KEY = 'ccb-state-v2';
  const MAX_IMG_DIM = 1600;          // longest side after auto-resize
  const JPEG_QUALITY = 0.92;
  const MIN_SCALE = 1;               // user scale (1× = exact "cover" fit)
  const MAX_SCALE = 4;
  const ABILITIES = ['str', 'dex', 'con', 'int', 'wis', 'cha'];
  const THEMES = ['parchment', 'obsidian', 'verdant', 'crimson', 'royal', 'signature'];
  const NAME_MAX_PX = 34;
  const NAME_MIN_PX = 14;
  const DEFAULT_PORTRAIT = 'https://cdn.shopify.com/s/files/1/0884/2389/1261/files/Custom_D_D_Character_Portrait_Warrior_Cleric_Art_FondlyFramed.webp?v=1746213617';

  const root = document.querySelector('[data-ccb-root]');
  if (!root) return;

  // ---- element refs ----
  const inputs = {};
  root.querySelectorAll('[data-ccb-input]').forEach((el) => {
    inputs[el.dataset.ccbInput] = el;
  });

  const previews = {};
  root.querySelectorAll('[data-ccb-preview]').forEach((el) => {
    previews[el.dataset.ccbPreview] = el;
  });

  const previewName = root.querySelector('#ccb-preview-name');
  const previewSubline = root.querySelector('#ccb-preview-subline');
  const previewQuote = root.querySelector('#ccb-preview-quote');

  const card = root.querySelector('#ccb-card');
  const portrait = root.querySelector('#ccb-portrait');
  const portraitImg = root.querySelector('#ccb-portrait-img');
  const portraitPlaceholder = root.querySelector('#ccb-portrait-placeholder');

  const upload = root.querySelector('#ccb-upload');
  const fileInput = root.querySelector('#ccb-file');
  const imageControls = root.querySelector('#ccb-image-controls');
  const clearImageBtn = root.querySelector('#ccb-clear-image');
  const resetImageBtn = root.querySelector('#ccb-reset-image');
  const uploadError = root.querySelector('#ccb-upload-error');

  const fillImageBtn = root.querySelector('#ccb-fill-image');
  const btnPng = root.querySelector('#ccb-download-png');
  const btnPdf = root.querySelector('#ccb-download-pdf');
  const btnReset = root.querySelector('#ccb-reset');

  // ---- state ----
  // Default state ships a fully-populated template character ("Ember", Forge
  // Cleric) so first-time visitors see the finished product immediately. Any
  // edit overwrites the corresponding field; Start over restores these defaults.
  function defaultState() {
    return {
      name: 'Ember',
      race: 'Aasimar (Protector)',
      klass: 'Cleric',
      level: '8',
      subclass: 'Forge Domain',
      nlKlass: false,
      nlSubclass: false,
      str: 16, dex: 10, con: 14, int: 10, wis: 20, cha: 14,
      hp: 64, ac: 18, speed: 30,
      weapon: 'Warhammer +1 (Blessed by the Forge)',
      signature: 'Spiritual Weapon',
      quote: 'Hold still. I can already see who you are.',
      portrait: DEFAULT_PORTRAIT,
      imgX: 0,
      imgY: 0,
      imgScale: 1,
      theme: 'parchment',
      fillImage: true
    };
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  // Auto-fit the character name to one line: short names render at the max
  // size (NAME_MAX_PX) and shrink to NAME_MIN_PX if needed to avoid wrapping.
  function fitName() {
    if (!previewName) return;
    if (!previewName.textContent) {
      previewName.style.fontSize = '';
      return;
    }
    previewName.style.whiteSpace = 'nowrap';
    const parent = previewName.parentElement;
    const maxWidth = parent ? parent.clientWidth : 0;
    if (maxWidth <= 0) return;
    let size = NAME_MAX_PX;
    previewName.style.fontSize = size + 'px';
    while (previewName.scrollWidth > maxWidth && size > NAME_MIN_PX) {
      size -= 0.5;
      previewName.style.fontSize = size + 'px';
    }
  }

  let state = loadState();

  function loadState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return defaultState();
      const parsed = JSON.parse(raw);
      return Object.assign(defaultState(), parsed);
    } catch (e) {
      return defaultState();
    }
  }

  function saveState() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      // quota or disabled — ignore
    }
  }

  // ---- rendering ----
  function applyStateToInputs() {
    Object.keys(inputs).forEach((key) => {
      const value = state[key];
      if (value !== undefined && value !== null) inputs[key].value = value;
    });
    applyBreakButtonsFromState();
    applyThemeFromState();
    applyFillFromState();
    setPortrait(state.portrait);
    applyImgTransform();
  }

  // ---- fill-image mode ----
  function applyFillFromState() {
    const filled = !!state.fillImage;
    card.classList.toggle('is-filled', filled);
    if (fillImageBtn) fillImageBtn.textContent = filled ? 'Revert' : 'Fill image';
  }

  function bindFillImageButton() {
    if (!fillImageBtn) return;
    fillImageBtn.addEventListener('click', () => {
      state.fillImage = !state.fillImage;
      applyFillFromState();
      // Container width just changed by ±36px, so the cover-fit baseline
      // and pan-clamp bounds need to be recalculated against the new size.
      if (state.portrait) recomputeBaseScale();
      saveState();
    });
  }

  // ---- themes ----
  function applyThemeFromState() {
    const theme = THEMES.indexOf(state.theme) >= 0 ? state.theme : 'parchment';
    card.setAttribute('data-theme', theme);
    root.querySelectorAll('[data-ccb-theme]').forEach((btn) => {
      const active = btn.dataset.ccbTheme === theme;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function bindThemeButtons() {
    root.querySelectorAll('[data-ccb-theme]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const next = btn.dataset.ccbTheme;
        if (THEMES.indexOf(next) < 0) return;
        state.theme = next;
        applyThemeFromState();
        saveState();
      });
    });
  }

  function breakStateKey(field) {
    if (field === 'klass') return 'nlKlass';
    if (field === 'subclass') return 'nlSubclass';
    return null;
  }

  function applyBreakButtonsFromState() {
    root.querySelectorAll('[data-ccb-break]').forEach((btn) => {
      const key = breakStateKey(btn.dataset.ccbBreak);
      if (!key) return;
      const active = !!state[key];
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function bindBreakButtons() {
    root.querySelectorAll('[data-ccb-break]').forEach((btn) => {
      const key = breakStateKey(btn.dataset.ccbBreak);
      if (!key) return;
      btn.addEventListener('click', () => {
        state[key] = !state[key];
        applyBreakButtonsFromState();
        renderPreview();
        saveState();
      });
    });
  }

  function renderPreview() {
    previewName.textContent = state.name || '';
    fitName();

    // Subline parts in render order. Each carries a `nl` flag — when true and
    // the part isn't first, a <br> is used in place of the " · " separator.
    const parts = [];
    if (state.race) parts.push({ text: state.race, nl: false });
    if (state.klass) parts.push({ text: state.klass, nl: !!state.nlKlass });
    if (state.level !== '' && state.level !== null && state.level !== undefined) {
      parts.push({ text: 'Level ' + state.level, nl: false });
    }
    if (state.subclass) parts.push({ text: state.subclass, nl: !!state.nlSubclass });

    let html = '';
    parts.forEach((p, i) => {
      if (i === 0) {
        html += escapeHtml(p.text);
        return;
      }
      html += p.nl ? '<br>' : ' · ';
      html += escapeHtml(p.text);
    });
    previewSubline.innerHTML = html;

    ABILITIES.forEach((key) => {
      if (previews[key]) previews[key].textContent = state[key];
    });
    ['hp', 'ac', 'speed'].forEach((key) => {
      if (previews[key]) previews[key].textContent = state[key];
    });
    ['weapon', 'signature'].forEach((key) => {
      if (previews[key]) previews[key].textContent = state[key] || '';
    });

    if (state.quote && String(state.quote).trim().length > 0) {
      previewQuote.textContent = '"' + state.quote + '"';
      previewQuote.style.display = '';
    } else {
      previewQuote.textContent = '';
      previewQuote.style.display = 'none';
    }

    boldTopTwoAbilities();
    updateExportButtons();
  }

  function boldTopTwoAbilities() {
    const ordered = ABILITIES
      .map((key, idx) => ({ key, idx, value: Number(state[key]) || 0 }))
      .sort((a, b) => (b.value - a.value) || (a.idx - b.idx));
    const topTwo = new Set([ordered[0].key, ordered[1].key]);
    ABILITIES.forEach((key) => {
      const el = previews[key];
      if (!el) return;
      el.style.fontWeight = topTwo.has(key) ? '500' : '400';
    });
  }

  function updateExportButtons() {
    const hasName = !!(state.name && String(state.name).trim().length > 0);
    btnPng.disabled = !hasName;
    btnPdf.disabled = !hasName;
  }

  // ---- portrait + transform ----
  // The image is positioned at its natural pixel size, anchored at the
  // container center via translate(-50%, -50%). On top of that we apply the
  // user's pan (imgX/Y) and a combined scale of (baseScale × imgScale). The
  // baseScale is computed on image load so that imgScale = 1 corresponds to
  // a cover-style fit (image fully covers the square, possibly cropped on one
  // axis). This lets the user drag at any zoom level to reveal the cropped
  // portions of the original — which is what people actually expect.
  let naturalW = 0;
  let naturalH = 0;
  let baseScale = 1;

  function setPortrait(src) {
    if (src) {
      portraitImg.hidden = false;
      portraitPlaceholder.style.display = 'none';
      imageControls.hidden = false;
      portrait.classList.add('is-loaded');
      if (portraitImg.src !== src) {
        portraitImg.src = src;
      } else {
        recomputeBaseScale();
      }
    } else {
      portraitImg.removeAttribute('src');
      portraitImg.hidden = true;
      portraitPlaceholder.style.display = '';
      imageControls.hidden = true;
      portrait.classList.remove('is-loaded');
      naturalW = 0;
      naturalH = 0;
      baseScale = 1;
    }
  }

  function recomputeBaseScale() {
    if (!portraitImg.naturalWidth || !portraitImg.naturalHeight) return;
    const rect = portrait.getBoundingClientRect();
    if (!rect.width) return;
    naturalW = portraitImg.naturalWidth;
    naturalH = portraitImg.naturalHeight;
    // cover: smaller image dim fills the (square) container
    baseScale = rect.width / Math.min(naturalW, naturalH);
    portraitImg.style.width = naturalW + 'px';
    portraitImg.style.height = naturalH + 'px';
    const clamped = clampTranslate(state.imgX, state.imgY, state.imgScale);
    state.imgX = clamped.x;
    state.imgY = clamped.y;
    applyImgTransform();
  }

  function clampTranslate(x, y, userScale) {
    if (!naturalW || !naturalH) return { x: 0, y: 0 };
    const rect = portrait.getBoundingClientRect();
    const displayedW = naturalW * baseScale * userScale;
    const displayedH = naturalH * baseScale * userScale;
    const maxX = Math.max(0, (displayedW - rect.width) / 2);
    const maxY = Math.max(0, (displayedH - rect.height) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y))
    };
  }

  function applyImgTransform() {
    if (!naturalW) {
      portraitImg.style.transform = '';
      return;
    }
    const s = (state.imgScale || 1) * baseScale;
    const x = state.imgX || 0;
    const y = state.imgY || 0;
    portraitImg.style.transform =
      'translate(calc(-50% + ' + x + 'px), calc(-50% + ' + y + 'px)) scale(' + s + ')';
  }

  function resetTransform() {
    state.imgX = 0;
    state.imgY = 0;
    state.imgScale = 1;
    applyImgTransform();
    saveState();
  }

  portraitImg.addEventListener('load', recomputeBaseScale);
  portraitImg.addEventListener('error', () => {
    // CORS / network failure on the default URL — fall back to empty state.
    state.portrait = '';
    setPortrait('');
    saveState();
  });
  // Container size may change with viewport — recompute baseScale & re-fit
  // the name on resize.
  window.addEventListener('resize', () => {
    if (state.portrait) recomputeBaseScale();
    fitName();
  });

  // ---- upload + resize ----
  function processImage(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('read-failed'));
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const w = img.naturalWidth;
          const h = img.naturalHeight;
          const longest = Math.max(w, h);
          const scale = longest > MAX_IMG_DIM ? MAX_IMG_DIM / longest : 1;
          const tw = Math.max(1, Math.round(w * scale));
          const th = Math.max(1, Math.round(h * scale));

          const canvas = document.createElement('canvas');
          canvas.width = tw;
          canvas.height = th;
          const ctx = canvas.getContext('2d');
          // Cream backdrop for any JPEG transparency-flattening.
          ctx.fillStyle = '#FAF6ED';
          ctx.fillRect(0, 0, tw, th);
          ctx.drawImage(img, 0, 0, tw, th);

          try {
            const out = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
            resolve(out);
          } catch (err) {
            reject(err);
          }
        };
        img.onerror = () => reject(new Error('decode-failed'));
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  }

  function handleFile(file) {
    uploadError.hidden = true;
    uploadError.textContent = '';
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      uploadError.textContent = 'Use a JPG, PNG, or WebP file.';
      uploadError.hidden = false;
      return;
    }
    processImage(file)
      .then((dataUrl) => {
        state.portrait = dataUrl;
        state.imgX = 0;
        state.imgY = 0;
        state.imgScale = 1;
        setPortrait(state.portrait);
        applyImgTransform();
        saveState();
      })
      .catch(() => {
        uploadError.textContent = "Couldn't read that image. Try a different file.";
        uploadError.hidden = false;
      });
  }

  // ---- listeners ----
  function bindInputs() {
    Object.keys(inputs).forEach((key) => {
      const el = inputs[key];
      el.addEventListener('input', () => {
        let value = el.value;
        if (el.type === 'number') {
          value = value === '' ? '' : Number(value);
          const min = el.min !== '' ? Number(el.min) : null;
          const max = el.max !== '' ? Number(el.max) : null;
          if (value !== '' && min !== null && value < min) value = min;
          if (value !== '' && max !== null && value > max) value = max;
          if (value === '') value = min !== null ? min : 0;
        }
        state[key] = value;
        renderPreview();
        saveState();
      });
    });
  }

  function bindUpload() {
    upload.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        fileInput.click();
      }
    });
    fileInput.addEventListener('change', () => {
      handleFile(fileInput.files && fileInput.files[0]);
      fileInput.value = '';
    });

    ['dragenter', 'dragover'].forEach((evt) => {
      upload.addEventListener(evt, (e) => {
        e.preventDefault();
        e.stopPropagation();
        upload.classList.add('is-dragover');
      });
    });
    ['dragleave', 'drop'].forEach((evt) => {
      upload.addEventListener(evt, (e) => {
        e.preventDefault();
        e.stopPropagation();
        upload.classList.remove('is-dragover');
      });
    });
    upload.addEventListener('drop', (e) => {
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) handleFile(file);
    });

    clearImageBtn.addEventListener('click', () => {
      state.portrait = '';
      state.imgX = 0;
      state.imgY = 0;
      state.imgScale = 1;
      setPortrait('');
      applyImgTransform();
      saveState();
    });

    resetImageBtn.addEventListener('click', resetTransform);
  }

  // ---- portrait drag + zoom ----
  function bindPortraitInteract() {
    let dragging = false;
    let activePointers = new Map(); // pointerId -> {x, y}
    let pinchStartDist = 0;
    let pinchStartScale = 1;
    let dragStart = null;

    function distance(a, b) {
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      return Math.sqrt(dx * dx + dy * dy);
    }

    portrait.addEventListener('pointerdown', (e) => {
      if (!state.portrait) return;
      portrait.setPointerCapture(e.pointerId);
      activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (activePointers.size === 1) {
        dragging = true;
        portrait.classList.add('is-dragging');
        dragStart = {
          x: e.clientX,
          y: e.clientY,
          tx: state.imgX,
          ty: state.imgY
        };
      } else if (activePointers.size === 2) {
        // Switch from pan to pinch zoom.
        dragging = false;
        const pts = Array.from(activePointers.values());
        pinchStartDist = distance(pts[0], pts[1]);
        pinchStartScale = state.imgScale;
      }
    });

    portrait.addEventListener('pointermove', (e) => {
      if (!activePointers.has(e.pointerId)) return;
      activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (activePointers.size === 2) {
        const pts = Array.from(activePointers.values());
        const dist = distance(pts[0], pts[1]);
        if (pinchStartDist > 0) {
          const next = pinchStartScale * (dist / pinchStartDist);
          state.imgScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, next));
          const clamped = clampTranslate(state.imgX, state.imgY, state.imgScale);
          state.imgX = clamped.x;
          state.imgY = clamped.y;
          applyImgTransform();
        }
        return;
      }

      if (dragging && dragStart) {
        const dx = e.clientX - dragStart.x;
        const dy = e.clientY - dragStart.y;
        const clamped = clampTranslate(dragStart.tx + dx, dragStart.ty + dy, state.imgScale);
        state.imgX = clamped.x;
        state.imgY = clamped.y;
        applyImgTransform();
      }
    });

    function endPointer(e) {
      if (activePointers.has(e.pointerId)) {
        activePointers.delete(e.pointerId);
        try { portrait.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
      }
      if (activePointers.size < 2) {
        pinchStartDist = 0;
      }
      if (activePointers.size === 0) {
        if (dragging) {
          dragging = false;
          portrait.classList.remove('is-dragging');
          saveState();
        } else {
          // pinch ended
          saveState();
        }
        dragStart = null;
      }
    }

    portrait.addEventListener('pointerup', endPointer);
    portrait.addEventListener('pointercancel', endPointer);

    portrait.addEventListener('wheel', (e) => {
      if (!state.portrait) return;
      e.preventDefault();
      const delta = -e.deltaY * 0.0025;
      const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, state.imgScale + delta));
      state.imgScale = next;
      const clamped = clampTranslate(state.imgX, state.imgY, state.imgScale);
      state.imgX = clamped.x;
      state.imgY = clamped.y;
      applyImgTransform();
      saveState();
    }, { passive: false });

    // Block native image drag-out.
    portrait.addEventListener('dragstart', (e) => e.preventDefault());
  }

  // ---- export ----
  function slugify(s) {
    return (s || 'character')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/['']/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'character';
  }

  function renderCardCanvas() {
    if (typeof html2canvas !== 'function') {
      return Promise.reject(new Error('html2canvas not loaded'));
    }
    return html2canvas(card, {
      scale: 3,
      backgroundColor: '#FAF6ED',
      useCORS: true,
      allowTaint: false,
      logging: false
    });
  }

  function downloadPng() {
    renderCardCanvas().then((canvas) => {
      const link = document.createElement('a');
      link.download = slugify(state.name) + '-character-card.png';
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }).catch((err) => {
      console.error('PNG export failed:', err);
      alert('Something went wrong exporting the PNG. Try removing and re-uploading your portrait.');
    });
  }

  function downloadPdf() {
    if (!window.jspdf || !window.jspdf.jsPDF) {
      alert('PDF library has not finished loading. Try again in a moment.');
      return;
    }
    renderCardCanvas().then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const { jsPDF } = window.jspdf;
      // Derive the page size from the actual rendered card aspect ratio so
      // the PDF stays sharp at whatever height the card lands on (the card
      // grows past 3:5 when content needs more room).
      const widthIn = 2.5;
      const heightIn = Math.round((widthIn * (canvas.height / canvas.width)) * 100) / 100;
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: [widthIn, heightIn]
      });
      pdf.addImage(imgData, 'PNG', 0, 0, widthIn, heightIn, undefined, 'FAST');
      pdf.save(slugify(state.name) + '-character-card.pdf');
    }).catch((err) => {
      console.error('PDF export failed:', err);
      alert('Something went wrong exporting the PDF. Try removing and re-uploading your portrait.');
    });
  }

  function resetAll() {
    if (!window.confirm('Clear all fields and start over?')) return;
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
    state = defaultState();
    applyStateToInputs();
    renderPreview();
  }

  // ---- custom number steppers ----
  function buildSteppers() {
    root.querySelectorAll('.ccb__form input[type="number"]').forEach((input) => {
      if (input.closest('.ccb__stepper')) return;

      const wrap = document.createElement('div');
      wrap.className = 'ccb__stepper';

      const minus = document.createElement('button');
      minus.type = 'button';
      minus.className = 'ccb__stepper-btn ccb__stepper-btn--minus';
      minus.setAttribute('aria-label', 'Decrease');
      minus.textContent = '−';

      const plus = document.createElement('button');
      plus.type = 'button';
      plus.className = 'ccb__stepper-btn ccb__stepper-btn--plus';
      plus.setAttribute('aria-label', 'Increase');
      plus.textContent = '+';

      input.parentNode.insertBefore(wrap, input);
      wrap.appendChild(minus);
      wrap.appendChild(input);
      wrap.appendChild(plus);

      function step(delta) {
        const cur = Number(input.value) || 0;
        const min = input.min !== '' ? Number(input.min) : -Infinity;
        const max = input.max !== '' ? Number(input.max) : Infinity;
        const next = Math.max(min, Math.min(max, cur + delta));
        if (next === cur) return;
        input.value = next;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }

      minus.addEventListener('click', () => step(-1));
      plus.addEventListener('click', () => step(1));
    });
  }

  // ---- init ----
  buildSteppers();
  bindInputs();
  bindBreakButtons();
  bindThemeButtons();
  bindFillImageButton();
  bindUpload();
  bindPortraitInteract();
  applyStateToInputs();
  renderPreview();

  btnPng.addEventListener('click', downloadPng);
  btnPdf.addEventListener('click', downloadPdf);
  btnReset.addEventListener('click', resetAll);
})();
